import React, { useState, useEffect, useMemo, useRef } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import "./invoiceMaker.css";

/* ========== CONFIGURATION ========== */
// ⚠️ REPLACE THIS WITH YOUR DEPLOYED GOOGLE APPS SCRIPT WEB APP URL
const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbzbp4NYnux6PJD_uN4YR_43WSkSEgnVaLUp9LEWzkHfpUHWW2Ww0G7RVu0MxFLVxzZZjA/exec"; 

/* ========== Utility: Number to Indian Words ========== */
const ones = [
  "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen",
];
const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function numberToIndianWords(num) {
  if (isNaN(num)) return "";
  const n = Math.floor(num);
  const paise = Math.round((num - n) * 100);

  const convertTwo = (x) => {
    if (x < 20) return ones[x];
    return tens[Math.floor(x / 10)] + (x % 10 ? " " + ones[x % 10] : "");
  };
  const convertThree = (x) => {
    let s = "";
    if (x >= 100) {
      s += ones[Math.floor(x / 100)] + " Hundred";
      x = x % 100;
      if (x) s += " And ";
    }
    if (x > 0) s += convertTwo(x);
    return s;
  };
  const convert = (x) => {
    if (x === 0) return "Zero";
    let s = "";
    const crore = Math.floor(x / 10000000);
    x = x % 10000000;
    if (crore) s += convertThree(crore) + " Crore ";
    const lakh = Math.floor(x / 100000);
    x = x % 100000;
    if (lakh) s += convertThree(lakh) + " Lakh ";
    const thou = Math.floor(x / 1000);
    x = x % 1000;
    if (thou) s += convertThree(thou) + " Thousand ";
    if (x > 0) {
      const h = Math.floor(x / 100);
      const rem = x % 100;
      if (h) s += ones[h] + " Hundred";
      if (rem) s += (h ? " And " : "") + convertTwo(rem);
    }
    return s.trim();
  };

  let words = "";
  if (n === 0) words = "Zero";
  else words = convert(n);

  let result = "INR " + words;
  if (paise > 0) {
    result += " And " + convertTwo(paise) + " Paise";
  }
  result += " Only";
  return result;
}

/* ========== Invoice Number Helpers ========== */
const FIN_YEAR = "26/27";

function nextInvoiceNumber(counter) {
  const n = String(counter).padStart(4, "0");
  return `HP-${n}-${FIN_YEAR}`;
}

/* ========== Main Component ========== */
export default function InvoiceMaker() {
  // State
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form State
  const [counter, setCounter] = useState(1);
  const [editId, setEditId] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [customerName, setCustomerName] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [gstYes, setGstYes] = useState(false);
  const [gstNumber, setGstNumber] = useState("");
  const [placeOfSupply, setPlaceOfSupply] = useState("33");
  const [eventAmount, setEventAmount] = useState("");
  const [addonsAmount, setAddonsAmount] = useState("");

  // UI State
  const [printInvoice, setPrintInvoice] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  const printRef = useRef(null);

  // Fetch Invoices on Mount
  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/invoices");
      const json = await res.json();
      if (json.success) {
        setInvoices(json.data);
        // Set counter based on highest existing invoice number if needed
        if (json.data.length > 0) {
          setCounter(json.data.length + 1);
        }
      }
    } catch (err) {
      console.error("Failed to fetch invoices", err);
    } finally {
      setLoading(false);
    }
  };

  // Calculations
  const taxableAmount = useMemo(() => {
    const v = parseFloat(eventAmount);
    return isNaN(v) ? 0 : v;
  }, [eventAmount]);

  const cgst = useMemo(() => +(taxableAmount * 0.09).toFixed(2), [taxableAmount]);
  const sgst = useMemo(() => +(taxableAmount * 0.09).toFixed(2), [taxableAmount]);
  const addons = useMemo(() => {
    const v = parseFloat(addonsAmount);
    return isNaN(v) ? 0 : v;
  }, [addonsAmount]);
  const grandTotal = useMemo(
    () => +(taxableAmount + cgst + sgst + addons).toFixed(2),
    [taxableAmount, cgst, sgst, addons]
  );
  const totalTax = +(cgst + sgst).toFixed(2);
  const amountInWords = useMemo(() => numberToIndianWords(grandTotal), [grandTotal]);

  const currentInvNo = useMemo(() => {
    if (editId) {
      const ed = invoices.find((i) => i._id === editId);
      if (ed) return ed.invoiceNo;
    }
    return nextInvoiceNumber(counter);
  }, [editId, counter, invoices]);

  const formattedDate = useMemo(() => {
    if (!date) return "";
    const [y, m, d] = date.split("-");
    return `${d}.${m}.${y}`;
  }, [date]);

  const resetForm = () => {
    setEditId(null);
    setCustomerName("");
    setCustomerAddress("");
    setContactNumber("");
    setGstYes(false);
    setGstNumber("");
    setPlaceOfSupply("33");
    setEventAmount("");
    setAddonsAmount("");
    setDate(new Date().toISOString().split('T')[0]);
  };

  const handleSave = async () => {
    if (!customerName.trim()) {
      alert("Please enter Customer Name");
      return;
    }
    if (!eventAmount) {
      alert("Please enter Private Event Cost amount");
      return;
    }

    const payload = {
      invoiceNo: currentInvNo,
      date: formattedDate,
      customerName,
      customerAddress,
      contactNumber,
      gstYes,
      gstNumber: gstYes ? gstNumber : "URP",
      placeOfSupply,
      rcm: "No",
      eventAmount: taxableAmount,
      cgst,
      sgst,
      addons,
      grandTotal,
      totalTax,
      amountInWords,
      sac: "998596",
    };

    try {
      // 1. Save to Backend Database
      const url = editId ? `/api/invoices/${editId}` : "/api/invoices";
      const method = editId ? "PUT" : "POST";
      
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      
      const json = await res.json();
      if (json.success) {
        alert(editId ? "Invoice updated successfully!" : "Invoice saved successfully!");
        
        // 2. Sync to Google Sheets (Fire & Forget)
        if (GOOGLE_SCRIPT_URL && GOOGLE_SCRIPT_URL !== "YOUR_GOOGLE_SCRIPT_URL_HERE") {
          const sheetPayload = {
            invoiceNo: payload.invoiceNo,
            date: payload.date,
            customerName: payload.customerName,
            contactNumber: payload.contactNumber,
            gstNumber: payload.gstNumber,
            eventAmount: payload.eventAmount,
            cgst: payload.cgst,
            sgst: payload.sgst,
            grandTotal: payload.grandTotal
          };

          // Using no-cors to avoid CORS issues with Google Scripts from browser
          fetch(GOOGLE_SCRIPT_URL, {
            method: "POST",
            mode: "no-cors", 
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(sheetPayload),
          }).catch((err) => console.warn("Google Sheet sync failed (non-critical):", err));
        }

        resetForm();
        fetchInvoices();
      } else {
        alert("Error: " + json.message);
      }
    } catch (err) {
      alert("Network Error: " + err.message);
    }
  };

  const handleEdit = (inv) => {
    setEditId(inv._id);
    const parts = inv.date.split(".");
    if (parts.length === 3) setDate(`${parts[2]}-${parts[1]}-${parts[0]}`);
    setCustomerName(inv.customerName);
    setCustomerAddress(inv.customerAddress);
    setContactNumber(inv.contactNumber);
    setGstYes(inv.gstYes);
    setGstNumber(inv.gstYes ? inv.gstNumber : "");
    setPlaceOfSupply(inv.placeOfSupply);
    setEventAmount(String(inv.eventAmount));
    setAddonsAmount(String(inv.addons || ""));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this invoice?")) {
      try {
        const res = await fetch(`/api/invoices/${id}`, { method: "DELETE" });
        const json = await res.json();
        if (json.success) {
          fetchInvoices();
        } else {
          alert("Delete failed");
        }
      } catch (err) {
        alert("Network error");
      }
    }
  };

  const handlePrint = (inv) => {
    setPrintInvoice(inv);
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;
    setIsDownloading(true);

    try {
      const element = printRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;

      pdf.addImage(imgData, "PNG", imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`Invoice_${printInvoice.invoiceNo.replace(/\//g, "-")}.pdf`);
    } catch (error) {
      console.error("PDF download failed:", error);
      alert("Failed to download PDF. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const closePreview = () => {
    setPrintInvoice(null);
  };

  // Search Logic
  const filteredInvoices = useMemo(() => {
    if (!searchQuery.trim()) return invoices;
    const q = searchQuery.toLowerCase();
    return invoices.filter(inv => 
      inv.customerName.toLowerCase().includes(q) ||
      inv.contactNumber.includes(q) ||
      inv.invoiceNo.toLowerCase().includes(q) ||
      inv.date.includes(q)
    );
  }, [invoices, searchQuery]);

  return (
    <div className="invoice-app">
      <h1 className="app-heading no-print">HEXAPLAY INVOICE MANAGER</h1>

      {/* ===== FORM CARD ===== */}
      <div className="form-card no-print">
        <div className="invoice-header">
          <div className="logo-box">
            <div className="logo-icon">
              <img src="/images/logo11.png" alt="Hexaplay Logo" className="form-logo-img" />
            </div>
            <div className="logo-text-wrap">
              <div className="logo-name">THE</div>
              <div className="logo-name">HEXAPLAY</div>
              <div className="logo-tag">UNIT OF NP GROUPS</div>
            </div>
          </div>
          <div className="header-center">
            <h1>INVOICE</h1>
            <div className="sub-title">THE HEXAPLAY (UNIT OF NP GROUPS)</div>
          </div>
          <div className="header-right">
            <div className="date-field">
              <label>DATE</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="inv-field">
              <label>INV.NO:</label>
              <span>{currentInvNo}</span>
            </div>
          </div>
        </div>

        <div className="invoice-body">
          <div className="company-details">
            <h2 className="section-title">Company Details</h2>
            <p>
              <strong>The Hexaplay (A Unit of NP Groups)</strong>
              <br />
              No.1/398, BMR Mall, Rajiv Gandhi Salai (OMR),
              <br />
              Navalur, Chennai - 600130
            </p>
            <p style={{ marginTop: 8 }}>
              <strong>GSTIN:</strong> 33AAYFN3600M1ZX
            </p>
          </div>

          <div className="bill-to">
            <h2 className="section-title">Bill To</h2>
            <div className="bill-to-form">
              <div className="form-row" style={{ textAlign: "left" }}>
                <label>Customer Name:</label>
                <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Enter customer name" />
              </div>
              <div className="form-row">
                <label>Address:</label>
                <input type="text" value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} placeholder="Enter address" />
              </div>
              <div className="form-row">
                <label>Contact Number:</label>
                <input type="tel" value={contactNumber} onChange={(e) => setContactNumber(e.target.value)} placeholder="+91-XXXXXXXXXX" />
              </div>
              <div className="form-row">
                <label>GST Number:</label>
                <div className="gst-radios">
                  <label>
                    <input type="radio" name="gst" checked={!gstYes} onChange={() => { setGstYes(false); setGstNumber(""); }} />
                    No
                  </label>
                  <label>
                    <input type="radio" name="gst" checked={gstYes} onChange={() => setGstYes(true)} />
                    Yes
                  </label>
                  {gstYes ? (
                    <input type="text" value={gstNumber} onChange={(e) => setGstNumber(e.target.value)} placeholder="Enter GSTIN" style={{ flex: 1, padding: "6px 10px", border: "1px solid #d0d4dc", borderRadius: 6, fontSize: 13 }} />
                  ) : (
                    <span className="urp-text">URP</span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <label>Place of Supply:</label>
                <input type="text" value={placeOfSupply} onChange={(e) => setPlaceOfSupply(e.target.value)} placeholder="e.g., 33" />
                <span style={{ fontWeight: 600, fontSize: 13, marginLeft: 8 }}>/ RCM: No</span>
              </div>
            </div>
          </div>
        </div>

        <div className="service-section">
          <h2 className="section-title">Service Description</h2>
          <div className="svc-input-grid">
            <div className="svc-field">
              <label>Description</label>
              <div className="fixed-val">Private Event Cost</div>
            </div>
            <div className="svc-field">
              <label>SAC Code</label>
              <div className="fixed-val">998596</div>
            </div>
            <div className="svc-field">
              <label>Private Event Cost (₹)</label>
              <input type="number" value={eventAmount} onChange={(e) => setEventAmount(e.target.value)} placeholder="Enter amount" />
            </div>
            <div className="svc-field">
              <label>Add-ons (Pure Agent Reimbursement) (₹)</label>
              <input type="number" value={addonsAmount} onChange={(e) => setAddonsAmount(e.target.value)} placeholder="Enter add-ons amount" />
            </div>
          </div>

          <table className="preview-table">
            <thead>
              <tr>
                <th style={{ width: "50%" }}>Description</th>
                <th>SAC</th>
                <th>Qty</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Private Event Cost</td>
                <td className="text-center">998596</td>
                <td className="text-center">1</td>
                <td className="num">{taxableAmount.toFixed(2)}</td>
                <td className="num">₹{taxableAmount.toFixed(2)}</td>
              </tr>
              <tr>
                <td colSpan="4">CGST @ 9%</td>
                <td className="num">₹{cgst.toFixed(2)}</td>
              </tr>
              <tr>
                <td colSpan="4">SGST @ 9%</td>
                <td className="num">₹{sgst.toFixed(2)}</td>
              </tr>
              <tr>
                <td colSpan="4">Add-ons (Pure Agent Reimbursement)</td>
                <td className="num">₹{addons.toFixed(2)}</td>
              </tr>
              <tr className="grand-total">
                <td colSpan="4" className="font-bold">Grand Total</td>
                <td className="num">₹{grandTotal.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <div className="words-box">
            <strong>Amount Chargeable (in words):</strong> {amountInWords}
          </div>

          <h3 className="section-title" style={{ marginTop: 18, fontSize: 15 }}>Tax Summary</h3>
          <table className="preview-table tax-summary">
            <thead>
              <tr>
                <th rowSpan="2">SAC</th>
                <th rowSpan="2">Taxable Value</th>
                <th colSpan="2">CGST</th>
                <th colSpan="2">SGST/UTGST</th>
                <th rowSpan="2">Total Tax Amount</th>
              </tr>
              <tr>
                <th>Rate</th>
                <th>Amount</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-center">998596</td>
                <td className="num">{taxableAmount.toFixed(2)}</td>
                <td className="text-center">9%</td>
                <td className="num">{cgst.toFixed(2)}</td>
                <td className="text-center">9%</td>
                <td className="num">{sgst.toFixed(2)}</td>
                <td className="num">{totalTax.toFixed(2)}</td>
              </tr>
              <tr className="total-row">
                <td className="text-center font-bold">Total</td>
                <td className="num">{taxableAmount.toFixed(2)}</td>
                <td className="text-center"></td>
                <td className="num">{cgst.toFixed(2)}</td>
                <td className="text-center"></td>
                <td className="num">{sgst.toFixed(2)}</td>
                <td className="num">{totalTax.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="form-actions">
          {editId && (
            <button className="btn btn-secondary" onClick={resetForm}>Cancel</button>
          )}
          <button className="btn btn-primary" onClick={handleSave}>
            {editId ? "Update Invoice" : "Save Invoice"}
          </button>
        </div>
      </div>

      {/* ===== Saved Invoices List ===== */}
      <div className="saved-card no-print">
        <div className="saved-header">
          <h2>Saved Invoices</h2>
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input 
              type="text" 
              placeholder="Search by Name, Date, Mobile, Inv No..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery("")}>✕</button>
            )}
          </div>
        </div>
        
        {loading ? (
          <p style={{textAlign:'center', padding: 20}}>Loading invoices...</p>
        ) : (
          <table className="saved-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th>Invoice No.</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Mobile</th>
                <th>Amount</th>
                <th style={{ width: 220 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.length === 0 ? (
                <tr className="empty-row">
                  <td colSpan="7">No invoices found.</td>
                </tr>
              ) : (
                filteredInvoices.map((inv, idx) => (
                  <tr key={inv._id}>
                    <td>{idx + 1}</td>
                    <td className="font-bold">{inv.invoiceNo}</td>
                    <td>{inv.date}</td>
                    <td>{inv.customerName}</td>
                    <td>{inv.contactNumber || "-"}</td>
                    <td className="num">₹{inv.grandTotal.toFixed(2)}</td>
                    <td>
                      <button className="btn btn-sm btn-edit" onClick={() => handleEdit(inv)}>Edit</button>
                      <button className="btn btn-sm btn-print" onClick={() => handlePrint(inv)}>Print</button>
                      <button className="btn btn-sm btn-delete" onClick={() => handleDelete(inv._id)}>Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ===== PRINT PREVIEW BAR ===== */}
      {printInvoice && (
        <div className="print-preview-bar no-print">
          <span> Print Preview - {printInvoice.invoiceNo}</span>
          <div className="preview-actions">
            <button className="btn-download-pdf" onClick={handleDownloadPDF} disabled={isDownloading}>
              {isDownloading ? "⏳ Generating PDF..." : "📥 Download PDF"}
            </button>
            <button onClick={closePreview}>✕ Close Preview</button>
          </div>
        </div>
      )}

      {/* ===== PRINT AREA (A4) ===== */}
      <div ref={printRef} className={`print-area ${printInvoice ? "visible" : ""}`}>
        {printInvoice && (
          <>
            {/* Print Header */}
            <div className="print-header">
              <div className="print-logo-left">
                <img src="/images/logo11.png" alt="Hexaplay Logo" className="print-logo-img" />
              </div>
              <div className="print-title-center" style={{ marginLeft: "100px" }}>
                <h1>INVOICE</h1>
                <div className="print-sub-title">THE HEXAPLAY (UNIT OF NP GROUPS)</div>
              </div>
              <div className="print-meta-right">
                <div className="meta-row">
                  <span className="meta-label">DATE:</span> {printInvoice.date}
                </div>
                <div className="meta-row">
                  <span className="meta-label">INV.NO:</span>
                  <span className="inv-no">{printInvoice.invoiceNo}</span>
                </div>
              </div>
            </div>

            {/* Company & Bill To */}
            <div className="print-body-grid">
              <div className="print-company-col">
                <h3 className="col-heading">COMPANY DETAILS</h3>
                <p className="company-name"><strong>The Hexaplay (A Unit of NP Groups)</strong></p>
                <p className="company-addr">No.1/398, BMR Mall, Rajiv Gandhi Salai (OMR),</p>
                <p className="company-addr">Navalur, Chennai - 600130</p>
                <p className="company-gstin"><strong>GSTIN:</strong> 33AAYFN3600M1ZX</p>
              </div>
              <div className="print-bill-col">
                <h3 className="col-heading">BILL TO</h3>
                <div className="bill-line" style={{ textAlign: "left" }}><strong>Customer Name:</strong> {printInvoice.customerName}</div>
                <div className="bill-line"><strong>Address:</strong> {printInvoice.customerAddress}</div>
                <div className="bill-line"><strong>Contact Number:</strong> {printInvoice.contactNumber}</div>
                <div className="bill-line"><strong>GST Number:</strong> {printInvoice.gstNumber}</div>
                <div className="bill-line"><strong>Place of Supply:</strong> {printInvoice.placeOfSupply} / RCM: {printInvoice.rcm}</div>
              </div>
            </div>

            {/* Service Description */}
            <h3 className="print-section-heading">SERVICE DESCRIPTION</h3>
            <table className="print-svc-table">
              <thead>
                <tr>
                  <th className="th-desc">DESCRIPTION</th>
                  <th className="th-center">SAC</th>
                  <th className="th-center">QTY</th>
                  <th className="th-right">RATE</th>
                  <th className="th-right">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="td-desc">Private Event Cost</td>
                  <td className="td-center">{printInvoice.sac}</td>
                  <td className="td-center">1</td>
                  <td className="td-right">₹{printInvoice.eventAmount.toFixed(2)}</td>
                  <td className="td-right">₹{printInvoice.eventAmount.toFixed(2)}</td>
                </tr>
                <tr>
                  <td className="td-desc" colSpan="4">CGST @ 9%</td>
                  <td className="td-right">₹{printInvoice.cgst.toFixed(2)}</td>
                </tr>
                <tr>
                  <td className="td-desc" colSpan="4">SGST @ 9%</td>
                  <td className="td-right">₹{printInvoice.sgst.toFixed(2)}</td>
                </tr>
                <tr>
                  <td className="td-desc" colSpan="4">Add-ons (Pure Agent Reimbursement)</td>
                  <td className="td-right">₹{(printInvoice.addons || 0).toFixed(2)}</td>
                </tr>
                <tr className="grand-total-row">
                  <td className="td-desc td-bold" colSpan="4">Grand Total</td>
                  <td className="td-right td-bold">₹{printInvoice.grandTotal.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>

            {/* Amount in Words */}
            <div className="print-words-box">
              <strong>Amount Chargeable (in words):</strong> {printInvoice.amountInWords}
            </div>

            {/* Tax Summary Table */}
            <h3 className="print-section-heading">TAX SUMMARY</h3>
            <table className="print-tax-summary-table">
              <thead>
                <tr>
                  <th rowSpan="2" className="tax-th">SAC</th>
                  <th rowSpan="2" className="tax-th">TAXABLE VALUE</th>
                  <th colSpan="2" className="tax-th">CGST</th>
                  <th colSpan="2" className="tax-th">SGST/UTGST</th>
                  <th rowSpan="2" className="tax-th">TOTAL TAX AMOUNT</th>
                </tr>
                <tr>
                  <th className="tax-th-sub">RATE</th>
                  <th className="tax-th-sub">AMOUNT</th>
                  <th className="tax-th-sub">RATE</th>
                  <th className="tax-th-sub">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="tax-td-center">{printInvoice.sac}</td>
                  <td className="tax-td-right tax-td-bold">{printInvoice.eventAmount.toFixed(2)}</td>
                  <td className="tax-td-center">9%</td>
                  <td className="tax-td-right tax-td-bold">{printInvoice.cgst.toFixed(2)}</td>
                  <td className="tax-td-center">9%</td>
                  <td className="tax-td-right tax-td-bold">{printInvoice.sgst.toFixed(2)}</td>
                  <td className="tax-td-right tax-td-bold">{printInvoice.totalTax.toFixed(2)}</td>
                </tr>
                <tr className="tax-total-row">
                  <td className="tax-td-center tax-td-bold">Total</td>
                  <td className="tax-td-right tax-td-bold">{printInvoice.eventAmount.toFixed(2)}</td>
                  <td className="tax-td-center"></td>
                  <td className="tax-td-right tax-td-bold">{printInvoice.cgst.toFixed(2)}</td>
                  <td className="tax-td-center"></td>
                  <td className="tax-td-right tax-td-bold">{printInvoice.sgst.toFixed(2)}</td>
                  <td className="tax-td-right tax-td-bold">{printInvoice.totalTax.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>

            {/* Declaration */}
            <div className="print-declaration">
              <p><strong>Declaration:</strong> GST applicable only on Private Event Cost. Balloon Decoration, Add-ons, and Food are reimbursement expenses collected on behalf of the Pure Agent Vendors.</p>
            </div>

            {/* Signature Section */}
            <div className="print-signature-section">
              <img src="/images/signature.jpg" alt="Authorized Signature" className="signature-img" />
              <div className="sig-line-bottom">Authorized Signature</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}